/**
 * SqlEditor — zero-dependency highlighted SQL editor with autocomplete.
 *
 * A textarea can't color its own text, so a highlighted <pre> renders UNDER a
 * transparent <textarea> (the classic overlay — both layers share identical box
 * metrics so the visible text + caret stay aligned). No editor library: this is
 * one bounded input on one page, and the self-host dashboard ships the exact
 * same pattern (its SQL GUI is the reference implementation for this file).
 *
 * Autocomplete = SQL keywords ∪ live suggestions from the caller (table names +
 * columns discovered from results). Tab/Enter accepts, Esc closes, ↑/↓ move.
 * Cmd/Ctrl+Enter (or the Run button inside the editor) fires onRun.
 */
import React, { useEffect, useRef, useState } from 'react';

export const SQL_KEYWORDS = ['SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'NULL', 'IS', 'IN', 'LIKE', 'GLOB',
  'LIMIT', 'OFFSET', 'ORDER', 'BY', 'GROUP', 'HAVING', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'CROSS',
  'ON', 'USING', 'AS', 'DISTINCT', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'TOTAL', 'ASC', 'DESC', 'BETWEEN',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'UNION', 'INTERSECT', 'EXCEPT', 'ALL', 'EXISTS', 'PRAGMA',
  'EXPLAIN', 'WITH', 'RECURSIVE', 'CAST', 'COALESCE', 'NULLIF', 'LENGTH', 'LOWER', 'UPPER', 'SUBSTR',
  'ROUND', 'ABS', 'DATE', 'DATETIME', 'STRFTIME', 'IFNULL', 'COLLATE', 'ESCAPE'];
const KW_SET = new Set(SQL_KEYWORDS);

const escHtml = (s) => String(s).replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));

// Tokenize SQL → highlighted HTML. Order: comment, string, number, word
// (keyword vs identifier), then any single char verbatim. The output is built
// exclusively from escHtml'd source, so it's safe to inject.
export function highlightSql(text) {
  let out = '';
  const re = /(--[^\n]*|\/\*[\s\S]*?\*\/)|('(?:[^']|'')*'|"(?:[^"]|"")*")|(\b\d+(?:\.\d+)?\b)|([A-Za-z_][A-Za-z0-9_]*)|([\s\S])/g;
  let m;
  while ((m = re.exec(text))) {
    if (m[1]) out += `<span class="tk-com">${escHtml(m[1])}</span>`;
    else if (m[2]) out += `<span class="tk-str">${escHtml(m[2])}</span>`;
    else if (m[3]) out += `<span class="tk-num">${escHtml(m[3])}</span>`;
    else if (m[4]) out += KW_SET.has(m[4].toUpperCase()) ? `<span class="tk-kw">${escHtml(m[4])}</span>` : escHtml(m[4]);
    else out += escHtml(m[0]);
  }
  return out;
}

export default function SqlEditor({ value, onChange, onRun, placeholder, getSuggestions }) {
  const wrapRef = useRef(null);
  const taRef = useRef(null);
  const preRef = useRef(null);
  // { items, idx, x, y } while the autocomplete popup is open, else null.
  const [ac, setAc] = useState(null);
  // Caret position to restore after an accept mutates the controlled value.
  const pendingCaretRef = useRef(null);

  const syncScroll = () => {
    const ta = taRef.current;
    const pre = preRef.current;
    if (ta && pre) { pre.scrollTop = ta.scrollTop; pre.scrollLeft = ta.scrollLeft; }
  };
  useEffect(() => {
    syncScroll();
    if (pendingCaretRef.current != null && taRef.current) {
      taRef.current.selectionStart = taRef.current.selectionEnd = pendingCaretRef.current;
      pendingCaretRef.current = null;
      taRef.current.focus();
    }
  }, [value]);

  const curWord = () => {
    const ta = taRef.current;
    if (!ta) return '';
    const m = /[A-Za-z_][A-Za-z0-9_]*$/.exec(ta.value.slice(0, ta.selectionStart));
    return m ? m[0] : '';
  };

  // Caret pixel position via a hidden mirror div (copies the textarea metrics).
  const caretXY = () => {
    const ta = taRef.current;
    const wrap = wrapRef.current;
    if (!ta || !wrap) return { x: 0, y: 0 };
    const cs = getComputedStyle(ta);
    const div = document.createElement('div');
    ['fontFamily', 'fontSize', 'fontWeight', 'lineHeight', 'letterSpacing', 'textTransform', 'paddingTop',
      'paddingRight', 'paddingBottom', 'paddingLeft', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth',
      'borderLeftWidth'].forEach((p) => { div.style[p] = cs[p]; });
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.left = '0';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordBreak = 'break-word';
    div.style.overflowWrap = 'break-word';
    div.style.overflow = 'hidden';
    div.style.boxSizing = 'border-box';
    div.style.width = `${ta.offsetWidth}px`;
    div.style.height = `${ta.offsetHeight}px`;
    div.textContent = ta.value.slice(0, ta.selectionStart);
    const span = document.createElement('span');
    span.textContent = '​';
    div.appendChild(span);
    wrap.appendChild(div);
    const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4;
    const x = span.offsetLeft - ta.scrollLeft;
    const y = span.offsetTop - ta.scrollTop + lh + 3;
    wrap.removeChild(div);
    return { x: Math.max(0, Math.min(x, Math.max(0, ta.clientWidth - 160))), y };
  };

  const openAc = () => {
    const w = curWord();
    if (w.length < 1) { setAc(null); return; }
    const lw = w.toLowerCase();
    let pool = SQL_KEYWORDS;
    try { pool = pool.concat((getSuggestions && getSuggestions()) || []); } catch { /* suggestions are best-effort */ }
    const seen = new Set();
    const matches = [];
    for (const x of pool) {
      const s = String(x);
      const ls = s.toLowerCase();
      if (ls !== lw && ls.startsWith(lw) && !seen.has(ls)) { seen.add(ls); matches.push(s); }
      if (matches.length >= 8) break;
    }
    if (!matches.length) { setAc(null); return; }
    const pos = caretXY();
    setAc({ items: matches, idx: 0, x: pos.x, y: pos.y });
  };

  const accept = (i) => {
    if (!ac || i < 0 || i >= ac.items.length) return;
    const ta = taRef.current;
    if (!ta) return;
    const word = curWord();
    const start = ta.selectionStart - word.length;
    const next = ta.value.slice(0, start) + ac.items[i] + ta.value.slice(ta.selectionStart);
    pendingCaretRef.current = start + ac.items[i].length;
    setAc(null);
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (ac && ac.items.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAc({ ...ac, idx: (ac.idx + 1) % ac.items.length }); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAc({ ...ac, idx: (ac.idx - 1 + ac.items.length) % ac.items.length }); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); accept(ac.idx); return; }
      if (e.key === 'Escape') { e.preventDefault(); setAc(null); return; }
    }
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); if (onRun) onRun(); }
  };

  return (
    <div className="sdb-ed" ref={wrapRef}>
      <pre className="sdb-hl" aria-hidden="true" ref={preRef}>
        <code dangerouslySetInnerHTML={{ __html: `${highlightSql(value || '')}\n` }} />
      </pre>
      <textarea
        className="sdb-ta"
        ref={taRef}
        spellCheck={false}
        placeholder={placeholder || 'SELECT * FROM …  (read-only — SELECT / PRAGMA)'}
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); requestAnimationFrame(openAc); }}
        onScroll={syncScroll}
        onBlur={() => setTimeout(() => setAc(null), 150)}
        onKeyDown={handleKeyDown}
      />
      <button type="button" className="sv-btn sdb-runbtn" onClick={onRun}>Run</button>
      {ac && (
        <div className="sdb-ac" style={{ left: ac.x, top: ac.y }}>
          {ac.items.map((item, i) => (
            <div
              key={item}
              className={`sdb-ac-item${i === ac.idx ? ' on' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); accept(i); }}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
